import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import UploadCertificateTemplate from './UploadCertificateTemplate';
import CertificateFillable from './CertificateFillable';
import './Certificate.css';
import {
  getCertificateTemplateList,
  deleteCertificateTemplate,
  uploadCSVGenerateCertificates
} from '../../../services/CommonService';

const Certificate = () => {
  const [certificateTemplateOffcanvas, setCertificateTemplateOffcanvas] = useState(false);
  const [certificateFillableOffcanvas, setCertificateFillableOffcanvas] = useState(false);
  const [pdfDisplayCanvas, setPdfDisplayCanvas] = useState('');
  const [templateUuid, setTemplateUuid] = useState('');
  const [certificateTemplates, setCertificateTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [csvUploading, setCsvUploading] = useState(false);
  const [uploadingTemplateId, setUploadingTemplateId] = useState(null);
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState(false);
  const [selectedTemplateForUpload, setSelectedTemplateForUpload] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchCertificateTemplates();
    // eslint-disable-next-line
  }, []);

  const fetchCertificateTemplates = async () => {
    setLoading(true);
    try {
      const response = await getCertificateTemplateList({});
      let templates = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        templates = response.data.data;
      } else if (Array.isArray(response?.data)) {
        templates = response.data;
      } else if (Array.isArray(response)) {
        templates = response;
      }
      setCertificateTemplates(templates);
    } catch {
      toast.error('Failed to fetch certificate templates');
      setCertificateTemplates([]);
    }
    setLoading(false);
  };

  const deleteTemplate = async (uuid) => {
    try {
      await deleteCertificateTemplate({ template_uuid: uuid });
      toast.success('Template deleted successfully!');
      fetchCertificateTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const filteredTemplates = certificateTemplates.filter(template =>
    (template.template_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (template.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleTemplateSelect = (template) => {
    setPdfDisplayCanvas(template.file_url);
    setTemplateUuid(template.uuid);
    setCertificateFillableOffcanvas(true);
  };

  const handleTemplatePreview = (template) => {
    setPreviewUrl(template.file_url || '');
    setPreviewOpen(true);
  };

  const handleDownloadCSV = (template) => {
    console.log('Download function called with template:', template);
    const url = template?.csv_url;
    console.log('CSV URL:', url);

    if (!url) {
      toast.error('CSV not available for this template');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = url;
      const safeName = (template.template_name || 'certificate_placeholders').toString().replace(/[^a-z0-9-_]+/gi, '_');
      link.download = `${safeName}.csv`;
      console.log('Creating download link:', link.href, 'with filename:', link.download);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log('Download link clicked');
    } catch (e) {
      console.error('Error in download:', e);
      window.open(url, '_blank');
    }
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      file.type !== 'text/csv' &&
      !file.name.endsWith('.csv')
    ) {
      toast.error('Please select a valid CSV file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadCSVGenerateCertificates(formData);

      if (response && response.data) {
        toast.success('CSV uploaded successfully! Certificates are being generated.');
        fetchCertificateTemplates();
      } else {
        toast.error('Failed to upload CSV');
      }
    } catch {
      toast.error('Failed to upload CSV. Please try again.');
    } finally {
      setCsvUploading(false);
      event.target.value = '';
    }
  };

  const handleTemplateCSVUpload = async (event, template) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      file.type !== 'text/csv' &&
      !file.name.endsWith('.csv')
    ) {
      toast.error('Please select a valid CSV file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setUploadingTemplateId(template.uuid);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_uuid', template.uuid);

      const response = await uploadCSVGenerateCertificates(formData);

      if (response && response.data) {
        toast.success(`CSV uploaded successfully for "${template.template_name}"! Certificates are being generated.`);
        fetchCertificateTemplates();
      } else {
        toast.error('Failed to upload CSV');
      }
    } catch {
      toast.error('Failed to upload CSV. Please try again.');
    } finally {
      setUploadingTemplateId(null);
      event.target.value = '';
    }
  };

  const handleModalCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedTemplateForUpload) return;

    if (
      file.type !== 'text/csv' &&
      !file.name.endsWith('.csv')
    ) {
      toast.error('Please select a valid CSV file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setUploadingTemplateId(selectedTemplateForUpload.uuid);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_uuid', selectedTemplateForUpload.uuid);

      const response = await uploadCSVGenerateCertificates(formData);

      if (response && response.data) {
        toast.success('CSV uploaded successfully!');
        fetchCertificateTemplates();
        setCsvUploadModalOpen(false);
        setSelectedTemplateForUpload(null);
      } else {
        toast.error('Failed to upload CSV');
      }
    } catch (error) {
      toast.error('Failed to upload CSV. Please try again.');
    } finally {
      setUploadingTemplateId(null);
      event.target.value = '';
    }
  };

  const handleUploadSuccess = (response) => {
    let fileUrl = '';
    let uuid = '';
    if (response) {
      if (response.file_url) {
        fileUrl = response.file_url;
        uuid = response.uuid || '';
      } else if (response.data?.file_url) {
        fileUrl = response.data.file_url;
        uuid = response.data.uuid || '';
      } else if (typeof response === 'string') {
        try {
          const parsedResponse = JSON.parse(response);
          fileUrl = parsedResponse.file_url || '';
          uuid = parsedResponse.uuid || '';
        } catch { }
      }
    }

    if (fileUrl) {
      setPdfDisplayCanvas(fileUrl);
      setTemplateUuid(uuid);
      setCertificateFillableOffcanvas(true);
      fetchCertificateTemplates();
    } else {
      toast.error('No file URL found after upload');
    }
  };

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <div className="certificate-management-container">
        {/* Header Section */}
        <div className="management-header d-flex justify-content-between">
          <div className="header-content">
            <h1>Certificate Management</h1>
          </div>
          <div>
            <button
              className="btn btn-primary upload-template-btn"
              onClick={() => setCertificateTemplateOffcanvas(true)}
            >
              <i className="bi bi-plus-lg"></i> Upload Template
            </button>
          </div>
        </div>


        {/* Controls Section */}
        <div className="management-controls">
          <div className="search-container">
            <div className="search-input-container">
              <i className="bi bi-search search-icon me-2"></i>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="templates-count">
              <span className="badge bg-primary">
                {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'}
              </span>
            </div>
          </div>
          {/* Main Content */}
          <div className="management-content">
            {
              filteredTemplates.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="bi bi-file-earmark-pdf"></i>
                  </div>
                  <h3>
                    {searchTerm ? 'No templates found' : 'No certificate templates yet'}
                  </h3>
                  <p>
                    {searchTerm
                      ? 'Try adjusting your search or browse all templates'
                      : 'Upload your first template to get started'}
                  </p>

                </div>
              ) : (
                <>

                  <div className="row mt-4">
                    {filteredTemplates.map((template) => (
                      <div key={template.uuid} className="col-xl-3 col-lg-6 col-md-4 col-sm-12 mb-2">
                        <div className="certificate-template-card">
                          <div className="certificate-template-card-header">
                            <h3 className="certificate-template-title">{template.template_name}</h3>
                          </div>

                          <div className="certificate-template-card-body">
                            <p className="template-description">
                              {template.description || 'This certificate is awarded to organizations that demonstrate...'}
                            </p>

                            <div className="template-meta">
                              <div className="meta-item">
                                <i className="bi bi-clock"></i>
                                <span>Created: {formatDate(template.created_at)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="certificate-template-card-footer">
                            <div className="actions">
                              <button
                                type="button"
                                className="btn btn-primary upload-btn"
                                onClick={() => {
                                  setSelectedTemplateForUpload(template);
                                  setCsvUploadModalOpen(true);
                                }}
                                disabled={uploadingTemplateId === template.uuid}
                                data-toggle="tooltip"
                                title="Upload CSV"
                              >
                                Upload CSV
                              </button>

                              {template.csv_url && (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary download-btn"
                                  onClick={() => {
                                    console.log('Download clicked for template:', template);
                                    console.log('CSV URL:', template.csv_url);
                                    handleDownloadCSV(template);
                                  }}
                                  data-toggle="tooltip"
                                  title="Download placeholders CSV"
                                >
                                  Download
                                </button>
                              )}
                            </div>

                            <div className="bottom-actions mt-2">
                              <button
                                className="btn btn-sm btn-outline-secondary action-btn"
                                onClick={() => handleTemplateSelect(template)}
                                data-toggle="tooltip"
                                title="Edit Certificate"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>

                              <button
                                className="btn btn-sm btn-outline-secondary action-btn"
                                onClick={() => handleTemplatePreview(template)}
                                data-toggle="tooltip"
                                title="Preview Certificate"
                              >
                                <i className="bi bi-eye"></i>
                              </button>

                              <button
                                className="btn btn-sm btn-outline-secondary action-btn delete-btn"
                                onClick={() => deleteTemplate(template.uuid)}
                                data-toggle="tooltip"
                                title="Delete Certificate"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>

                            <input
                              id={`csv-upload-${template.uuid}`}
                              type="file"
                              accept=".csv"
                              onChange={(e) => handleTemplateCSVUpload(e, template)}
                              className="hidden-file-input"
                              disabled={uploadingTemplateId === template.uuid}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
          </div>
        </div>

        {/* CSV Upload Modal */}
        {csvUploadModalOpen && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Upload CSV for {selectedTemplateForUpload?.template_name}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setCsvUploadModalOpen(false);
                      setSelectedTemplateForUpload(null);
                    }}
                  ><i className="bi bi-x-circle"></i></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="csv-file-input" className="form-label">Select CSV File</label>
                    <input
                      id="csv-file-input"
                      type="file"
                      accept=".csv"
                      onChange={handleModalCSVUpload}
                      className="form-control"
                      disabled={uploadingTemplateId === selectedTemplateForUpload?.uuid}
                    />

                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setCsvUploadModalOpen(false);
                      setSelectedTemplateForUpload(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={uploadingTemplateId === selectedTemplateForUpload?.uuid}
                  >
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewOpen && (
          <div className="preview-modal" role="dialog" aria-modal="true">
            <div className="preview-backdrop" onClick={() => setPreviewOpen(false)}></div>
            <div className="preview-modal-content">
              <div className="preview-modal-header">
                <h5 className="mb-0">Template Preview</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setPreviewOpen(false)}></button>
              </div>
              <div className="preview-modal-body">
                {previewUrl ? (
                  <iframe src={previewUrl} title="Template Preview" className="preview-frame"></iframe>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">No preview available</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Offcanvas Components */}
        <UploadCertificateTemplate
          isOpen={certificateTemplateOffcanvas}
          onClose={() => setCertificateTemplateOffcanvas(false)}
          onUploadSuccess={handleUploadSuccess}
        />
        <CertificateFillable
          isOpen={certificateFillableOffcanvas}
          onClose={() => {
            setCertificateFillableOffcanvas(false);
            setPdfDisplayCanvas('');
            setTemplateUuid('');
          }}
          pdfDisplayCanvas={pdfDisplayCanvas}
          templateUuid={templateUuid}
          onPlaceholdersSaved={fetchCertificateTemplates}
        />

      </div>
    </>
  );
};

export default Certificate;